/**
 * Session storage helpers for persisting pre-auth idea inputs.
 * Used by landing flow to stash and later retrieve idea/rubric selections.
 */
import type { OnboardingChatMessage, OnboardingResult } from "@/lib/onboarding/schema";
import {
  SS_PENDING_IDEA,
  SS_PENDING_IDEA_BACKUP,
  SS_ONBOARDING_CHAT,
  SS_ONBOARDING_CHAT_BACKUP,
  SS_ONBOARDING_SESSION_BACKUP,
  SS_ONBOARDING_CLAIM_TOKEN,
} from "@/lib/storage-keys";

/** Surfaces that stash a pending idea (Build-in-Lev handoffs + direct stashers). */
export type OnboardingHandoffSource =
  | "idea-finder"
  | "zeitgeist"
  | "landing-chat"
  | "ip-machine"
  // The team-only "Skip to the canvas" on a program lander, for testing.
  | "program-test-skip";

export type PendingIdeaPayload = {
  idea: string;
  rubric: "venture" | "bootstrap";
  /** Structured onboarding data from chat flow (optional for backward compatibility) */
  onboardingResult?: OnboardingResult;
  /** Which surface stashed this idea — optional (stashes written before 2026-08-14 lack it). */
  source?: OnboardingHandoffSource;
};

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function savePendingIdea(payload: PendingIdeaPayload): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(SS_PENDING_IDEA, JSON.stringify(payload));
  } catch (error) {
    console.warn("Unable to save pending idea", error);
    /* benign: sessionStorage unavailable in private mode */
  }
}

export function consumePendingIdea(): PendingIdeaPayload | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_PENDING_IDEA);
    if (!raw) return null;
    // Backup before removing so IdeaChatInput can offer restore if user navigates back
    window.sessionStorage.setItem(SS_PENDING_IDEA_BACKUP, raw);
    window.sessionStorage.removeItem(SS_PENDING_IDEA);
    return JSON.parse(raw) as PendingIdeaPayload;
  } catch (error) {
    console.warn("Unable to consume pending idea", error);
    /* benign: sessionStorage unavailable in private mode */
    return null;
  }
}

export function peekPendingIdea(): PendingIdeaPayload | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_PENDING_IDEA);
    if (!raw) return null;
    return JSON.parse(raw) as PendingIdeaPayload;
  } catch (error) {
    console.warn("Unable to read pending idea", error);
    /* benign: sessionStorage unavailable in private mode */
    return null;
  }
}

// Chat persistence for streaming onboarding flow

export type OnboardingChatPayload = {
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  result?: OnboardingResult;
};

export function saveOnboardingChat(payload: OnboardingChatPayload): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(SS_ONBOARDING_CHAT, JSON.stringify(payload));
  } catch (error) {
    console.warn("Unable to save onboarding chat", error);
    /* benign: sessionStorage unavailable in private mode */
  }
}

export function peekOnboardingChat(): OnboardingChatPayload | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_ONBOARDING_CHAT);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingChatPayload;
  } catch (error) {
    console.warn("Unable to read onboarding chat", error);
    /* benign: sessionStorage unavailable in private mode */
    return null;
  }
}

export function consumeOnboardingChat(): OnboardingChatPayload | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_ONBOARDING_CHAT);
    if (!raw) return null;
    // Backup before removing so IdeaChatInput can offer restore if user navigates back
    window.sessionStorage.setItem(SS_ONBOARDING_CHAT_BACKUP, raw);
    window.sessionStorage.removeItem(SS_ONBOARDING_CHAT);
    return JSON.parse(raw) as OnboardingChatPayload;
  } catch (error) {
    console.warn("Unable to consume onboarding chat", error);
    /* benign: sessionStorage unavailable in private mode */
    return null;
  }
}

// Full-session conversion backup (lost-idea fix)
//
// The claimed server session is the primary conversion source, but its cookie
// can be missing at claim time. This backup mirrors the whole onboarding
// session (result + messages + materials) in sessionStorage so /onboarding/
// complete can still build a project when the claim yields nothing. Written on
// every update and at the build/skip handoff; cleared only after a project is
// created. sessionStorage survives the same-tab sign-up redirect.

export type OnboardingSessionBackup = {
  /**
   * `rubricType` is optional here on purpose: the backup is merged enrich-only
   * over the stored session result at claim time, and the untouched schema
   * default must stay ABSENT so it cannot overwrite a stored choice
   * (see saveOnboardingSessionBackup).
   */
  result: Omit<OnboardingResult, "rubricType"> & {
    rubricType?: OnboardingResult["rubricType"];
  };
  messages: OnboardingChatMessage[];
  materials?: unknown;
  /** Stable id for backup-only create-or-return (LEV-1360). */
  fallbackProjectId?: string;
  /**
   * True when the client actually observed `rubricType` on a streamed/merged
   * partial (not just the schema default). Optional so older sessionStorage
   * rows still parse.
   */
  rubricTypeDirty?: boolean;
};

function mintFallbackProjectId(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 25);
}

export function ensureOnboardingSessionBackupFallbackId(
  backup: OnboardingSessionBackup,
): OnboardingSessionBackup {
  if (backup.fallbackProjectId) return backup;
  const withFallbackId = { ...backup, fallbackProjectId: mintFallbackProjectId() };
  saveOnboardingSessionBackup(withFallbackId);
  return withFallbackId;
}

/**
 * Drop an untouched `rubricType: "venture"` so the backup stays enrich-only.
 *
 * Belt-and-suspenders against a model that echoes "venture" without being
 * asked: keeping such an echo would let the claim's backup merge overwrite a
 * `bootstrap` the server session already recorded. `"bootstrap"` is never a
 * default, so it always survives. (The schema is presence-preserving since
 * PR #688, so an untouched field normally never reaches the backup at all.)
 *
 * `rubricTypeDirty` is the client-side proof that a streamed partial actually
 * supplied the field. When it is set, a real `"venture"` choice must stay in
 * the backup — otherwise a failed persist would freeze the stale stored
 * bootstrap on recovery.
 */
function stripUntouchedRubricDefault(
  backup: OnboardingSessionBackup,
): OnboardingSessionBackup {
  if (backup.result.rubricType !== "venture") return backup;
  if (backup.rubricTypeDirty) return backup;
  const { rubricType: _untouchedDefault, ...result } = backup.result;
  return { ...backup, result };
}

export function saveOnboardingSessionBackup(backup: OnboardingSessionBackup): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(
      SS_ONBOARDING_SESSION_BACKUP,
      JSON.stringify(stripUntouchedRubricDefault(backup)),
    );
  } catch (error) {
    console.warn("Unable to save onboarding session backup", error);
    /* benign: sessionStorage unavailable in private mode */
  }
}

export function peekOnboardingSessionBackup(): OnboardingSessionBackup | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_ONBOARDING_SESSION_BACKUP);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingSessionBackup;
  } catch (error) {
    console.warn("Unable to read onboarding session backup", error);
    /* benign: sessionStorage unavailable in private mode */
    return null;
  }
}

export function clearOnboardingSessionBackup(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(SS_ONBOARDING_SESSION_BACKUP);
  } catch {
    /* benign: sessionStorage unavailable, best-effort cleanup */
  }
}

// Backup restore — if main keys are absent but backups exist, copy backups to main keys.
// Called on IdeaChatInput mount so the existing restore prompt can offer "Resume your
// previous conversation?" when session state was consumed by a prior page load.

export function restoreOnboardingFromBackup(): boolean {
  if (!isBrowser()) return false;
  let restored = false;
  try {
    if (!window.sessionStorage.getItem(SS_ONBOARDING_CHAT)) {
      const chatBackup = window.sessionStorage.getItem(SS_ONBOARDING_CHAT_BACKUP);
      if (chatBackup) {
        window.sessionStorage.setItem(SS_ONBOARDING_CHAT, chatBackup);
        restored = true;
      }
    }
    if (!window.sessionStorage.getItem(SS_PENDING_IDEA)) {
      const ideaBackup = window.sessionStorage.getItem(SS_PENDING_IDEA_BACKUP);
      if (ideaBackup) {
        window.sessionStorage.setItem(SS_PENDING_IDEA, ideaBackup);
        restored = true;
      }
    }
  } catch (error) {
    console.warn("Unable to restore onboarding from backup", error);
    /* benign: sessionStorage unavailable in private mode */
  }
  return restored;
}

// The anon path never consumes SS_PENDING_IDEA (canvas lifecycle early-returns
// when a Big Idea block already exists), so claim is the one place it is safe
// to drop it — a stale key would auto-generate into any future blank canvas.
export function clearPendingIdea(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(SS_PENDING_IDEA);
  } catch {
    /* benign: sessionStorage unavailable */
  }
}

export function clearOnboardingBackups(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(SS_PENDING_IDEA_BACKUP);
    window.sessionStorage.removeItem(SS_ONBOARDING_CHAT_BACKUP);
    window.sessionStorage.removeItem(SS_ONBOARDING_SESSION_BACKUP);
    // A claim token (ADR 041) must never outlive its conversation: once the
    // session is converted or abandoned, a stale token re-sent on a later
    // /onboarding/complete visit would claim the wrong session.
    window.sessionStorage.removeItem(SS_ONBOARDING_CLAIM_TOKEN);
  } catch {
    /* benign: sessionStorage unavailable, best-effort cleanup */
  }
}

const SESSION_DETACH_TIMEOUT_MS = 10_000;
// Deadline for the onboarding-session mint POST. Without it a stalled request
// leaves the mint promise pending forever, and any caller that awaits it before
// the PDF parser's own timeout (handleFile → ensureSession) hangs the
// uploader with no error (#534 review). On timeout the mint resolves to "not
// usable" and the caller re-mints or takes the error path.
export const SESSION_MINT_TIMEOUT_MS = 10_000;

export type OnboardingSessionMint =
  | { usable: true; claimToken: string | null } // token only on a CREATE response
  | { usable: false };

/**
 * POST to mint (or idempotently reuse) the anonymous onboarding session
 * cookie. Never rejects: every failure path (caller abort, the 10 s deadline,
 * a non-OK status, unparsable JSON, a network error) resolves `{usable:
 * false}` so a caller can await this fire-and-forget. Never stores or logs
 * the claim token it returns — a token may only be stashed after the caller
 * verifies its own success path (Build-in-Lev stashes only after a verified
 * finalize PATCH, ADR 041 §2); this function just hands it back.
 */
export function mintOnboardingSession(opts: {
  signal: AbortSignal;
  posthogDistinctId: string | undefined;
}): Promise<OnboardingSessionMint> {
  const { signal, posthogDistinctId } = opts;
  // Caller abort and the deadline are the SAME AbortController here (unlike
  // postMaterialsParse's separate-signals split): a mint has no "stale but
  // recoverable" state to protect, so collapsing them is safe and simpler.
  const deadline = new AbortController();
  const abortForCaller = () => deadline.abort();
  if (signal.aborted) abortForCaller();
  signal.addEventListener("abort", abortForCaller, { once: true });
  const timer = setTimeout(() => deadline.abort(), SESSION_MINT_TIMEOUT_MS);
  return (async (): Promise<OnboardingSessionMint> => {
    try {
      const res = await fetch("/api/onboarding/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ posthogDistinctId }),
        signal: deadline.signal,
      });
      if (!res.ok) return { usable: false };
      const data = (await res.json().catch(() => null)) as
        | { ok?: boolean; token?: string; created?: boolean }
        | null;
      if (data?.ok !== true) return { usable: false };
      return { usable: true, claimToken: data.created && data.token ? data.token : null };
    } catch {
      /* benign: caller abort, deadline timeout, or network error — all unusable */
      return { usable: false };
    } finally {
      // The deadline MUST stay armed through the body read (`res.json()`
      // above): clearing it right after the fetch settles, as
      // postMaterialsParse's `finally` does, would let a hung body read run
      // past the documented 10 s bound with nothing left to cancel it.
      clearTimeout(timer);
      signal.removeEventListener("abort", abortForCaller);
    }
  })();
}

/**
 * Drop this browser's onboarding session cookie ("Start over"). Returns true
 * only when the server confirmed it; on false the browser may still be bound
 * to the old session, so the caller MUST NOT present a fresh conversation.
 */
export async function detachOnboardingSession(): Promise<boolean> {
  if (!isBrowser()) return false;
  // Bounded: the reset dialog stays busy until this settles, so a stalled
  // request would lock both of its buttons with no retry path.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SESSION_DETACH_TIMEOUT_MS);
  try {
    const res = await fetch("/api/onboarding/session", {
      method: "DELETE",
      credentials: "same-origin",
      signal: controller.signal,
    });
    return res.ok;
  } catch {
    /* benign: network error or timeout — false tells the caller to keep the old conversation */
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Server-session finalize push

/**
 * Push the client's final accumulated onboarding result (and transcript) onto
 * the server-side onboarding session, so the post-signup claim sees everything
 * the founder saw. Covers the Skip flow (whose result enrichment is
 * client-side only) and fields the model didn't re-emit on the last turn.
 * Cookie-authenticated; returns false on any failure. Build-path callers may
 * proceed degraded (per-turn persists cover the essentials), but Skip-path
 * callers MUST NOT navigate on failure — the enrichment exists only here, so
 * they surface a retry instead (see LandingHero.handleComplete).
 */
export async function pushFinalOnboardingResult(payload: {
  result: OnboardingResult;
  messages?: Array<{ role: "user" | "assistant"; content: string }>;
}): Promise<boolean> {
  if (!isBrowser()) return false;
  try {
    const res = await fetch("/api/onboarding/session", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch (error) {
    console.warn("Unable to push final onboarding result", error);
    /* benign: network error, gracefully fail with fallback return false */
    return false;
  }
}

// Field extraction helpers for OnboardingResult

export type ExtractedOnboardingFields = {
  /** Full idea text for context/generation (refinedIdea preferred) */
  idea: string;
  /** Short project name (oneLiner preferred, max 60 chars) */
  name: string;
  /** Rubric type for project */
  rubric: "venture" | "bootstrap";
};

/**
 * Extract idea, name, and rubric from an OnboardingResult.
 * - idea: refinedIdea > rawIdea > oneLiner > problem (full context — the
 *   oneLiner is a NAME and must never shadow a rich rawIdea; skip/handoff
 *   shapes carry the founder's whole idea in rawIdea with a null refinedIdea)
 * - name: oneLiner > idea (truncated to 60 chars for project name)
 * - rubric: rubricType with "venture" default
 */
export function extractFieldsFromResult(
  result: OnboardingResult,
): ExtractedOnboardingFields {
  const idea =
    result.bigIdea?.refinedIdea ||
    result.bigIdea?.rawIdea ||
    result.bigIdea?.oneLiner ||
    result.bigIdea?.problem ||
    "Untitled idea";

  const name =
    result.bigIdea?.oneLiner?.trim().slice(0, 60) ||
    idea.trim().slice(0, 60) ||
    "Untitled Project";

  const rubric = result.rubricType ?? "venture";

  return { idea, name, rubric };
}
